import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Input,
  InputNumber,
  Icon,
  message,
  Slider,
  Tooltip,
  Button,
  Progress,
  Spin
} from 'antd'
import { config } from '../../../../../constants'
import AudioHistoryModal from './AudioHistoryModal'
import './VoiceoverModal.scss'

const { TextArea } = Input
const AUDIO_PREVIEW_BASE_URL = config.videoGenerationUrl + 'audio/'

const getModeLabel = isEasyAudioMode =>
  isEasyAudioMode ? 'Narration' : 'Dialogue'

const clampNumber = (value, min, max) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return min
  return Math.min(max, Math.max(min, parsed))
}

const formatTime = seconds => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

const AudioTransport = ({ src }) => {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return undefined

    const syncTime = () => setCurrentTime(audio.currentTime || 0)
    const syncDuration = () => setDuration(audio.duration || 0)
    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    audio.addEventListener('loadedmetadata', syncDuration)
    audio.addEventListener('durationchange', syncDuration)
    audio.addEventListener('timeupdate', syncTime)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('loadedmetadata', syncDuration)
      audio.removeEventListener('durationchange', syncDuration)
      audio.removeEventListener('timeupdate', syncTime)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    audio.currentTime = 0
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
  }, [src])

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
      }
    }
  }, [])

  const togglePlayback = () => {
    const audio = audioRef.current
    if (!audio || !src) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }

    audio.play().catch(() => message.error('Playback failed'))
    setIsPlaying(true)
  }

  const handleSeek = event => {
    const audio = audioRef.current
    if (!audio || !duration) return

    const nextTime = Number(event.target.value)
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  if (!src) {
    return (
      <div className="voiceover-transport voiceover-transport--empty">
        <div className="voiceover-transport__empty-copy">
          Generated audio will appear here after you run voiceover creation.
        </div>
      </div>
    )
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="voiceover-transport">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        className="voiceover-transport__play-btn"
        onClick={togglePlayback}
      >
        <Icon type={isPlaying ? 'pause' : 'caret-right'} />
      </button>
      <div className="voiceover-transport__timeline">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step="0.01"
          value={currentTime}
          onChange={handleSeek}
          className="voiceover-transport__range"
          style={{ '--progress': `${progress}%` }}
        />
        <div className="voiceover-transport__time">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  )
}

const SettingControl = ({
  label,
  tip,
  value,
  onChange,
  min,
  max,
  step,
  marks
}) => {
  const handleNumberChange = nextValue => {
    if (nextValue == null || nextValue === '') return
    onChange(clampNumber(nextValue, min, max))
  }

  return (
    <div className="voiceover-setting">
      <div className="voiceover-setting__head">
        <div className="voiceover-setting__label">
          <span>{label}</span>
          <Tooltip title={tip}>
            <Icon type="question-circle" />
          </Tooltip>
        </div>
        <InputNumber
          value={value}
          min={min}
          max={max}
          step={step}
          precision={step < 1 ? 1 : 0}
          onChange={handleNumberChange}
          className="voiceover-setting__input"
        />
      </div>
      <Slider
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        marks={marks}
        tipFormatter={null}
      />
    </div>
  )
}

const VoiceoverModal = ({
  visible,
  scene,
  voices = [],
  selectedVoice: globalSelectedVoice,
  onClose,
  formData,
  onGenerate,
  onNavigateToStep,
  isClusteringAudio,
  audioClusteringProgress = 0,
  audioClusteringStatus = '',
  audioMode,
  onStopGeneration,
  onRestoreAudio,
  openHistoryOnOpen = false
}) => {
  const [voiceText, setVoiceText] = useState('')
  const [speedFactor, setSpeedFactor] = useState(0.7)
  const [exaggeration, setExaggeration] = useState(0.5)
  const [cfgWeight, setCfgWeight] = useState(0.8)
  const [customPrompt, setCustomPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [showStopConfirm, setShowStopConfirm] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)
  const [previewPlaying, setPreviewPlaying] = useState(null)
  const previewRef = useRef(null)

  const sceneKey = scene?.id
  const isEasyAudioMode = audioMode === 'narration'
  const hasAudio = Boolean(scene?.audio_link)
  const audioHistory = Array.isArray(scene?.audio_history)
    ? scene.audio_history.filter(item => item?.url)
    : []
  const charCount = voiceText?.length || 0
  const overLimit = charCount > 5000

  useEffect(() => {
    if (visible && scene && !voiceText) {
      const draft = scene.aggregated_voiceover
      setVoiceText(typeof draft === 'string' ? draft : draft?.text || '')
    }
  }, [visible])

  useEffect(() => {
    return () => {
      if (previewRef.current) {
        previewRef.current.pause()
      }
    }
  }, [])

  useEffect(() => {
    if (!visible) {
      setShowHistoryModal(false)
      setShowAdvancedSettings(false)
      if (previewRef.current) {
        previewRef.current.pause()
        previewRef.current = null
      }
      setPreviewPlaying(null)
    }
  }, [visible])

  useEffect(() => {
    if (visible && openHistoryOnOpen) {
      setShowHistoryModal(true)
    }
  }, [visible, openHistoryOnOpen])

  const handleGenerate = async () => {
    if (!globalSelectedVoice) {
      message.warning('Select a voice first')
      return
    }

    if (!(voiceText && voiceText.trim())) {
      message.warning('Enter voiceover text')
      return
    }

    setIsGenerating(true)
    try {
      await onGenerate({
        voice_id: globalSelectedVoice,
        text: voiceText,
        scene_structure: formData?.shotList,
        scene_key: sceneKey,
        speed_factor: speedFactor,
        exaggeration,
        cfg_weight: cfgWeight,
        prompt: customPrompt
      })
      message.success('Voiceover generated')
    } catch (e) {
      message.error(e?.message || 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  const togglePreview = id => {
    if (previewPlaying === id) {
      if (previewRef.current) {
        previewRef.current.pause()
      }
      previewRef.current = null
      setPreviewPlaying(null)
      return
    }

    if (previewRef.current) {
      previewRef.current.pause()
    }

    const audio = new Audio(`${AUDIO_PREVIEW_BASE_URL}${id}`)
    audio.play().catch(() => message.error('Preview failed'))
    audio.onended = () => setPreviewPlaying(null)
    previewRef.current = audio
    setPreviewPlaying(id)
  }

  const voiceName = id => {
    if (!id) return null
    const voice = voices.find(
      v => v?.id === id || v?.voice_id === id || v === id
    )
    return voice?.name || voice?.label || id
  }

  const handleRestoreAudio = audioVersion => {
    if (!onRestoreAudio) return
    onRestoreAudio(audioVersion)
    setShowHistoryModal(false)
  }

  const advancedControls = [
    {
      key: 'speed',
      label: 'Speed',
      tip: '0.5x = half speed, 2.0x = double speed',
      value: speedFactor,
      setValue: value => setSpeedFactor(clampNumber(value, 0.5, 2.0)),
      min: 0.5,
      max: 2.0,
      step: 0.1,
      marks: { 0.5: '0.5', 1.0: '1.0', 2.0: '2.0' }
    },
    {
      key: 'exaggeration',
      label: 'Exaggeration',
      tip: 'Controls expression intensity',
      value: exaggeration,
      setValue: value => setExaggeration(clampNumber(value, 0.1, 2.0)),
      min: 0.1,
      max: 2.0,
      step: 0.1,
      marks: { 0.1: '0.1', 1.0: '1.0', 2.0: '2.0' }
    },
    {
      key: 'cfg',
      label: 'CFG Weight',
      tip: 'Controls how closely audio follows your script',
      value: cfgWeight,
      setValue: value => setCfgWeight(clampNumber(value, 0.0, 1.0)),
      min: 0.0,
      max: 1.0,
      step: 0.1,
      marks: { 0.0: '0.0', 0.5: '0.5', 1.0: '1.0' }
    }
  ]

  if (isClusteringAudio) {
    return (
      <>
        <Modal
          visible={visible}
          footer={null}
          width={460}
          maskClosable={false}
          onCancel={onClose}
          centered
          bodyStyle={{ padding: 0 }}
          wrapClassName="voiceover-modal-progress-wrap"
        >
          <div className="voiceover-progress">
            <div className="voiceover-progress__spinner">
              <Spin size="large" />
            </div>
            <h3 className="voiceover-progress__title">Generating Audio</h3>
            <p className="voiceover-progress__text">
              {isEasyAudioMode
                ? 'Building your narration track.'
                : 'Rebuilding narration and dialogue tracks.'}
            </p>
            <Progress
              percent={audioClusteringProgress}
              status={
                audioClusteringStatus === 'failed' ? 'exception' : 'active'
              }
            />
            {audioClusteringStatus && (
              <div className="voiceover-progress__status">
                {audioClusteringStatus}
              </div>
            )}
            <Button
              className="voiceover-progress__stop-btn"
              onClick={() => setShowStopConfirm(true)}
            >
              Stop Generation
            </Button>
          </div>
        </Modal>

        <Modal
          visible={showStopConfirm}
          onCancel={() => setShowStopConfirm(false)}
          footer={null}
          width={420}
          centered
          bodyStyle={{ padding: 0 }}
          wrapClassName="voiceover-modal-stop-wrap"
        >
          <div className="voiceover-stop-confirm">
            <div className="voiceover-stop-confirm__icon">
              <Icon type="exclamation-circle" />
            </div>
            <h3 className="voiceover-stop-confirm__title">
              Stop audio generation?
            </h3>
            <p className="voiceover-stop-confirm__text">
              This generation request will be closed immediately. The request
              cost will still apply.
            </p>
            <div className="voiceover-stop-confirm__actions">
              <Button onClick={() => setShowStopConfirm(false)}>
                Continue
              </Button>
              <Button
                type="primary"
                className="voiceover-stop-confirm__danger-btn"
                onClick={() => {
                  if (onStopGeneration) {
                    onStopGeneration()
                  }
                  setShowStopConfirm(false)
                  onClose()
                }}
              >
                Stop
              </Button>
            </div>
          </div>
        </Modal>
      </>
    )
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={isGenerating ? undefined : onClose}
        footer={null}
        width={1080}
        maskClosable={!isGenerating}
        destroyOnClose
        centered
        bodyStyle={{ padding: 0 }}
        wrapClassName="voiceover-modal-wrap"
      >
        <div className="voiceover-modal">
          <div className="voiceover-modal__shell">
            <header className="voiceover-modal__header">
              <div className="voiceover-modal__header-main">
                <div className="voiceover-modal__header-icon">
                  <Icon type="sound" />
                </div>
                <div className="voiceover-modal__header-copy">
                  <h2>Voiceover Studio</h2>
                  <p>
                    {scene?.title || 'Scene'} · {getModeLabel(isEasyAudioMode)}
                  </p>
                </div>
              </div>

              {hasAudio && audioHistory.length > 0 && (
                <Button
                  className="voiceover-modal__history-btn"
                  onClick={() => setShowHistoryModal(true)}
                >
                  <Icon type="history" />
                  History ({audioHistory.length})
                </Button>
              )}
            </header>

            <div className="voiceover-modal__layout">
              <div className="voiceover-modal__content-column">
                <section className="voiceover-panel voiceover-panel--editor">
                  <div className="voiceover-panel__head">
                    <div>
                      <h3>Narration text</h3>
                      <p>Use the approved script for this scene voiceover.</p>
                    </div>
                  </div>

                  <div className="voiceover-editor">
                    <TextArea
                      rows={14}
                      value={voiceText}
                      onChange={e => setVoiceText(e.target.value)}
                      placeholder="Enter the voiceover script..."
                      disabled={isGenerating || !globalSelectedVoice}
                      className="voiceover-editor__textarea"
                    />
                    <div
                      className={`voiceover-editor__counter ${
                        overLimit ? 'voiceover-editor__counter--error' : ''
                      }`}
                    >
                      {charCount.toLocaleString()} / 5,000
                    </div>
                  </div>

                  {overLimit && (
                    <div className="voiceover-panel__error">
                      Exceeds 5,000 character limit.
                    </div>
                  )}
                </section>

                <section className="voiceover-panel voiceover-panel--prompt">
                  <div className="voiceover-panel__head">
                    <div>
                      <h3>Custom prompt</h3>
                      <p>
                        Optional guidance for tone, emotion, pacing, or delivery
                        style.
                      </p>
                    </div>
                  </div>

                  <div className="voiceover-editor voiceover-editor--prompt">
                    <TextArea
                      rows={6}
                      value={customPrompt}
                      onChange={e => setCustomPrompt(e.target.value)}
                      placeholder="Add extra instructions for voice tone, emotion, pacing, or style..."
                      disabled={isGenerating || !globalSelectedVoice}
                      className="voiceover-editor__textarea voiceover-editor__textarea--prompt"
                    />
                  </div>
                </section>
              </div>

              <aside className="voiceover-modal__sidebar">
                <section className="voiceover-panel voiceover-panel--sidebar">
                  <div className="voiceover-panel__head">
                    <div>
                      <h3>Configuration</h3>
                      <p>
                        Review your voice, tune generation, and monitor output.
                      </p>
                    </div>
                  </div>

                  {!globalSelectedVoice ? (
                    <section className="voiceover-modal__alert voiceover-modal__alert--warning">
                      <div className="voiceover-modal__alert-icon">
                        <Icon type="exclamation-circle" />
                      </div>
                      <div className="voiceover-modal__alert-copy">
                        <h3>No voice selected</h3>
                        <p>Choose a voice from the Voiceover step first.</p>
                      </div>
                      <Button
                        className="voiceover-modal__secondary-btn"
                        onClick={() => {
                          onClose()
                          if (onNavigateToStep) {
                            onNavigateToStep('voiceover')
                          }
                        }}
                      >
                        Select voice
                      </Button>
                    </section>
                  ) : (
                    <div className="voiceover-voice-card">
                      <div className="voiceover-voice-card__copy">
                        <span className="voiceover-voice-card__eyebrow">
                          Voice picker
                        </span>
                        <strong>{voiceName(globalSelectedVoice)}</strong>
                      </div>
                      <button
                        type="button"
                        className="voiceover-voice-card__preview"
                        onClick={() => togglePreview(globalSelectedVoice)}
                      >
                        <Icon
                          type={
                            previewPlaying === globalSelectedVoice
                              ? 'pause-circle'
                              : 'play-circle'
                          }
                        />
                        <span>
                          {previewPlaying === globalSelectedVoice
                            ? 'Pause'
                            : 'Play'}
                        </span>
                      </button>
                    </div>
                  )}

                  <section className="voiceover-settings">
                    <button
                      type="button"
                      className="voiceover-settings__toggle"
                      onClick={() =>
                        setShowAdvancedSettings(current => !current)
                      }
                      aria-expanded={showAdvancedSettings}
                    >
                      <div className="voiceover-settings__toggle-copy">
                        <h4>Advanced settings</h4>
                        <p>
                          Adjust delivery and prompt adherence only if needed.
                        </p>
                      </div>
                      <span className="voiceover-settings__toggle-icon">
                        <Icon type={showAdvancedSettings ? 'up' : 'down'} />
                      </span>
                    </button>

                    {showAdvancedSettings && (
                      <div className="voiceover-settings__body">
                        {advancedControls.map(control => (
                          <SettingControl
                            key={control.key}
                            label={control.label}
                            tip={control.tip}
                            value={control.value}
                            onChange={control.setValue}
                            min={control.min}
                            max={control.max}
                            step={control.step}
                            marks={control.marks}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                </section>

                <div className="voiceover-sidebar-footer">
                  <section className="voiceover-panel voiceover-panel--footer">
                    <div className="voiceover-panel__head voiceover-panel__head--footer">
                      <div>
                        <h3>Current audio</h3>
                        <p>Latest generated result</p>
                      </div>
                    </div>

                    <AudioTransport src={scene?.audio_link} />

                    {isGenerating && (
                      <div className="voiceover-modal__info-bar">
                        <Icon type="loading" spin />
                        <span>
                          {isEasyAudioMode
                            ? 'Generating voiceover...'
                            : 'Generating dialogue tracks...'}
                        </span>
                      </div>
                    )}
                  </section>
                </div>
              </aside>
            </div>
          </div>

          <div className="voiceover-modal__footer">
            <div className="voiceover-modal__footer-actions">
              <Button onClick={onClose} disabled={isGenerating}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleGenerate}
                loading={isGenerating}
                disabled={
                  !globalSelectedVoice ||
                  !(voiceText && voiceText.trim()) ||
                  overLimit
                }
                className="voiceover-modal__primary-btn"
              >
                {isGenerating ? 'Generating Voiceover' : 'Generate Voiceover'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <AudioHistoryModal
        visible={showHistoryModal}
        history={audioHistory}
        currentAudioUrl={scene?.audio_link}
        onCancel={() => setShowHistoryModal(false)}
        onSelect={handleRestoreAudio}
      />
    </>
  )
}

export default VoiceoverModal
